import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { formatDayMonthYear } from "@/lib/dates";
import { GRACE_DAYS, purgeDate } from "@/lib/deletionGrace";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AddIcon from "@mui/icons-material/Add";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useShallow } from "zustand/react/shallow";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Button } from "components/common/Button";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { LoadingState } from "components/common/LoadingState";
import { Tooltip } from "@/components/common/Tooltip";
import { Callout } from "components/common/Callout";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { TooltipButton } from "components/common/TooltipButton";
import { SectionRule } from "components/common/SectionRule";
import { Badge } from "components/common/Badge";
import { ImpactRow, plural } from "components/common/ImpactRow";
import { TextInput } from "components/common/TextInput";
import { navigatorButtonClasses } from "components/common/navigatorStyles";
import { EditableName } from "components/canvas/utils/EditableName";
import { nameLength } from "@/components/main/utils/naming";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  cancelSpaceDeletion,
  deleteSpace,
  getSpaceDeletionAnalysis,
  type SpaceDeletionAnalysis,
  fetchOrgMembers,
  fetchSpaceGrants,
  grantSpaceRole,
  isAbandoned,
  revokeSpaceRole,
  sealSpace,
  unsealSpace,
  updateSpace,
  type OrgMember,
  type SpaceGrant,
} from "@/lib/api/organizations";
import { AssignManagerDialog } from "./AssignManagerDialog";
import { SpaceColorPicker } from "./SpaceColorPicker";
import { NewSpaceOverlay } from "./NewSpaceOverlay";
import { MAX_SPACE_NAME_CHARS, MIN_SPACE_NAME_CHARS } from "./spaceName";
import { ColumnBottomSpacer } from "@/components/main/pages/pageColumn";
import {
  EmailChipInput,
  type EmailChip,
} from "components/shareSection/EmailChipInput";
import { GovernanceAuditList } from "@/components/main/governance/GovernanceAuditList";
import { GovernanceAuditPanel } from "@/components/main/governance/GovernanceAuditPanel";
import { useGovernanceAudit } from "@/components/main/governance/useGovernanceAudit";
import { CATALOG, MANAGE_ACCESS_MENU_LABEL } from "@/actions/catalog";

const ROLES = ["manager", "collaborator", "viewer"] as const;

/// Rows in the inline preview. The panel shows the full log, paged.
const AUDIT_PREVIEW_ROWS = 5;

/// One shared width so the Danger row reads as a set of equals rather than
/// three buttons sized by their own label.
const DANGER_BUTTON_WIDTH = "w-32";

/// What the Access row shows for this grant — the toast has to agree with it,
/// and `subject` is a client_id nobody recognises.
const grantLabel = (grant: SpaceGrant) =>
  grant.name || grant.email || grant.subject;

const ONLY_MANAGER_HINT = "only manager";

const roleLabel = (role: string) =>
  role.charAt(0).toUpperCase() + role.slice(1);

const doorMessage = (error: unknown, fallback: string) =>
  axios.isAxiosError(error) && typeof error.response?.data === "string"
    ? error.response.data
    : fallback;

/// Space settings (S6d, mock B) — the v1 surface: rename + color, the access
/// list (grant, role changes, revoke, guest chips per D26), the one real
/// configuration fact (sealed or inheriting), the governance strip with the
/// audit preview, and delete. Classification/wall/export arrive with S7.
export const SpaceSettingsPage: React.FC = () => {
  const { space_id } = useParams();
  const navigate = useNavigate();
  const { spaces, spacesLoading, refreshSpaces, activeOrg } =
    useOrgContextStore(
      useShallow((s) => ({
        spaces: s.spaces,
        spacesLoading: s.spacesLoading,
        refreshSpaces: s.refreshSpaces,
        activeOrg: s.activeOrg,
      })),
    );
  const space = spaces.find((s) => s.space_id === space_id);
  const parent = spaces.find((s) => s.space_id === space?.parent_space_id);
  const org = activeOrg();

  // The caller's view only (visible, untrashed models) — it can under-count,
  // so hiding the seal affordance is a courtesy and the server refusal stays
  // authoritative. The delete dialog never reads these: it renders the
  // server's pre-flight (D50).
  const models = useMainStateStore((s) => s.availableModels);
  const visibleModelCount = useMemo(
    () => models.filter((m) => m.space_id === space_id).length,
    [models, space_id],
  );
  const childSpaceCount = useMemo(
    () => spaces.filter((s) => s.parent_space_id === space_id).length,
    [spaces, space_id],
  );
  const looksEmpty = visibleModelCount === 0 && childSpaceCount === 0;

  const [grants, setGrants] = useState<SpaceGrant[]>([]);
  const [parentGrants, setParentGrants] = useState<SpaceGrant[]>([]);
  const [auditPanelOpen, setAuditPanelOpen] = useState(false);
  const [revoking, setRevoking] = useState<SpaceGrant | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [grantChips, setGrantChips] = useState<EmailChip[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantRole, setGrantRole] = useState<string>("collaborator");
  const [grantRoleOpen, setGrantRoleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createInsideOpen, setCreateInsideOpen] = useState(false);
  // D50: the dialog renders the server's pre-flight, and the delete carries
  // its token — the confirm is a contract about WHAT goes, not a dialog over
  // whatever this client could see.
  const [analysis, setAnalysis] = useState<SpaceDeletionAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [typedName, setTypedName] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [unsealOpen, setUnsealOpen] = useState(false);
  const grantRoleAnchorRef = useRef<HTMLButtonElement>(null);

  const selfId = useMemo(() => localStorage.getItem("clientID") ?? "", []);
  // Which grantees govern this space through the ORG rather than through the
  // grant beside their name: an admin's "Collaborator" row is not the whole
  // story, and nothing here used to say so.
  const orgRoleByEmail = useMemo(
    () =>
      new Map(
        members
          .filter((m) => m.role === "owner" || m.role === "org_admin")
          .map((m) => [m.email, m.role]),
      ),
    [members],
  );

  const guestCount = useMemo(
    () => grants.filter((g) => g.is_org_member === false).length,
    [grants],
  );

  // Who the delete actually costs. The presser is excluded — they know.
  const losingAccess = useMemo(
    () =>
      grants
        .filter((g) => g.subject !== `user:${selfId}`)
        .map((g) => ({
          key: `${g.subject}-${g.role}`,
          label: grantLabel(g),
          role: g.role,
        })),
    [grants, selfId],
  );

  const reloadGrants = useCallback(() => {
    if (!space_id) return;
    void fetchSpaceGrants(space_id).then(setGrants);
  }, [space_id]);

  // D19: a company org's spaces reach its members and nobody else, so the
  // grant field offers the roster and refuses anything outside it. A personal
  // org is one member, so it keeps free entry for its D26 guests.
  const orgId = org && !org.is_personal ? org.org_id : null;
  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      return;
    }
    let stale = false;
    void fetchOrgMembers(orgId).then((rows) => {
      if (!stale) setMembers(rows);
    });
    return () => {
      stale = true;
    };
  }, [orgId]);

  // Invited-but-not-yet-joined holds no FGA member tuple until they accept
  // (D43), so the grant door would refuse them.
  const joinedMembers = useMemo(
    () => members.filter((m) => !m.pending && m.email),
    [members],
  );
  const directory = useMemo(
    () =>
      orgId
        ? joinedMembers.map((m) => ({
            email: m.email,
            displayName: m.display_name,
          }))
        : undefined,
    [orgId, joinedMembers],
  );
  const clientIdByEmail = useMemo(
    () =>
      new Map(joinedMembers.map((m) => [m.email.toLowerCase(), m.client_id])),
    [joinedMembers],
  );
  const grantedEmails = useMemo(
    () => grants.map((g) => g.email).filter((e): e is string => !!e),
    [grants],
  );
  const currentUserEmail = useMemo(
    () => localStorage.getItem("email") || "",
    [],
  );

  useEffect(() => {
    reloadGrants();
  }, [reloadGrants]);

  // Inherited section: the parent's grants, when the caller can read them —
  // fetchSpaceGrants resolves [] on a refusal, which hides the section.
  useEffect(() => {
    if (!space?.parent_space_id || space.sealed) {
      setParentGrants([]);
      return;
    }
    void fetchSpaceGrants(space.parent_space_id).then(setParentGrants);
  }, [space?.parent_space_id, space?.sealed]);

  // 403 (not an org admin) hides Governance; a personal org has no admin
  // distinct from its owner, so the D3 strip would describe you to yourself.
  // Scoped to THIS space — the org's own log lives on the org settings page.
  const audit = useGovernanceAudit(
    org && !org.is_personal ? org.org_id : null,
    space_id,
    AUDIT_PREVIEW_ROWS,
  );

  if (!space) {
    return spacesLoading || spaces.length === 0 ? (
      <LoadingState label="Loading space…" />
    ) : (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <span className="text-fg-regular">This space does not exist here.</span>
        <Button variant="default" onClick={() => navigate("/overview")}>
          Back to overview
        </Button>
      </div>
    );
  }

  // `EditableName.commit` runs synchronously and reads the return value, so
  // the PUT is fired off and its result handled later.
  const commitName = (name: string): boolean => {
    const trimmed = name.trim();
    if (trimmed === space.name) return true;
    if (nameLength(trimmed) < MIN_SPACE_NAME_CHARS) {
      showNotificationGlobal(
        `Space name must be at least ${MIN_SPACE_NAME_CHARS} characters`,
        "error",
      );
      return false;
    }
    void updateSpace(space.space_id, { name: trimmed }).then((updated) => {
      if (updated) void refreshSpaces();
      else showNotificationGlobal("Rename failed", "error");
    });
    return true;
  };

  const pickColor = async (color: string) => {
    const updated = await updateSpace(space.space_id, { color });
    if (updated) await refreshSpaces();
    else showNotificationGlobal("Color change failed", "error");
  };

  const submitGrant = async () => {
    const emails = grantChips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);
    if (emails.length === 0) {
      setGrantError(
        grantChips.length === 0
          ? "Select a recipient to grant access to."
          : "Resolve the highlighted addresses first.",
      );
      return;
    }
    setGrantError(null);

    // A roster address grants by client_id, which skips the door's email
    // lookup; a guest (personal org only) has none, so it goes by address.
    const refused: string[] = [];
    let lastError: unknown = null;
    for (const email of emails) {
      const clientId = clientIdByEmail.get(email.toLowerCase());
      try {
        await grantSpaceRole(
          space.space_id,
          clientId ? { user: clientId } : { email },
          grantRole,
        );
      } catch (error) {
        refused.push(email);
        lastError = error;
      }
    }

    setGrantChips([]);
    reloadGrants();
    if (refused.length > 0) {
      showNotificationGlobal(
        `${doorMessage(lastError, "Grant failed")} — not granted: ${refused.join(", ")}`,
        "error",
      );
    }
    const granted = emails.filter((email) => !refused.includes(email));
    if (granted.length > 0) {
      showNotificationGlobal(
        `${granted.join(", ")} now has ${roleLabel(grantRole)} access to "${space.name}"`,
        "success",
      );
    }
  };

  const changeRole = async (grant: SpaceGrant, role: string) => {
    const user = grant.subject.replace(/^user:/, "");
    try {
      await grantSpaceRole(space.space_id, { user }, role);
      try {
        await revokeSpaceRole(space.space_id, user, grant.role);
      } catch (error) {
        // The door refuses a sole manager stepping down (D66), so a demotion
        // can fail HALF-applied — undo the new role rather than leave the
        // grantee holding both.
        await revokeSpaceRole(space.space_id, user, role).catch(() => {});
        throw error;
      }
      reloadGrants();
      showNotificationGlobal(
        `${grantLabel(grant)} is now ${roleLabel(role)}`,
        "success",
      );
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Role change failed"), "error");
      reloadGrants();
    }
  };

  // Stranding a space is recoverable — `can_manage_access` composes from the
  // ORG, so a governor can always reappoint — but it is the whole point of this
  // click rather than a side effect, so it is confirmed rather than refused.
  const strandsTheSpace = (grant: SpaceGrant) =>
    grant.role === "manager" &&
    grants.filter((g) => g.role === "manager").length === 1 &&
    !looksEmpty;

  // D66: the door refuses this. A parent's manager — you included — still
  // manages this space after the revoke.
  const isYourOnlyManagerGrant = (grant: SpaceGrant) =>
    grant.subject === `user:${selfId}` &&
    grant.role === "manager" &&
    !grants.some((g) => g.role === "manager" && g.subject !== grant.subject) &&
    !parentGrants.some((g) => g.role === "manager");

  const removeAccess = async (grant: SpaceGrant) => {
    const user = grant.subject.replace(/^user:/, "");
    try {
      await revokeSpaceRole(space.space_id, user, grant.role);
      showNotificationGlobal(
        `${grantLabel(grant)} no longer has access to "${space.name}"`,
        "success",
      );
      // Revoking your OWN grant can take the space off your rail entirely —
      // unless you govern the org, which keeps it visible. The server decides,
      // so re-read rather than predict, and leave a page you can no longer see.
      if (user === selfId) {
        await refreshSpaces();
        const stillVisible = useOrgContextStore
          .getState()
          .spaces.some((s) => s.space_id === space.space_id);
        if (!stillVisible) {
          navigate("/overview");
          return;
        }
      }
      reloadGrants();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Revoke failed"), "error");
    }
  };

  const loadAnalysis = async () => {
    setAnalysis(null);
    setAnalysisError(null);
    try {
      setAnalysis(await getSpaceDeletionAnalysis(space.space_id));
    } catch (error) {
      setAnalysisError(doorMessage(error, "Could not analyse the space"));
    }
  };

  const openDelete = () => {
    setTypedName("");
    setDeleteOpen(true);
    void loadAnalysis();
  };

  const confirmDelete = async () => {
    if (!analysis) return;
    try {
      const { purge_at, spaces_scheduled } = await deleteSpace(
        space.space_id,
        analysis.confirm_token,
      );
      const subSpaces = Math.max(spaces_scheduled.length - 1, 0);
      showNotificationGlobal(
        subSpaces > 0
          ? `"${space.name}" and ${subSpaces} ${plural(subSpaces, "sub-space")} are in the Trash until ${formatDayMonthYear(purge_at)}.`
          : `"${space.name}" is in the Trash until ${formatDayMonthYear(purge_at)}.`,
        "success",
      );
      setDeleteOpen(false);
      // Deliberately staying: the space is still live, and this page is where
      // the countdown and the cancel live.
      await refreshSpaces();
    } catch (error) {
      // A stale token (the space changed under us) comes back as a refusal;
      // re-read so the dialog shows the current picture before a retry.
      showNotificationGlobal(doorMessage(error, "Delete failed"), "error");
      void loadAnalysis();
    }
  };

  const undoDeletion = async () => {
    try {
      await cancelSpaceDeletion(space.space_id);
      showNotificationGlobal(
        `Deletion of "${space.name}" cancelled`,
        "success",
      );
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(
        doorMessage(error, "Could not cancel the deletion"),
        "error",
      );
    }
  };

  // D28. Seal: managers, empty spaces only (the door carries the counts).
  // Un-seal: org admins only — in a personal org the owner is the admin; in a
  // company org the audit fetch succeeding is the admin signal. The server
  // enforces either way.
  const canUnseal = (org?.is_personal ?? false) || audit.available === true;
  // Matches the Danger section's own gate, so the Configuration hint can never
  // promise a seal the page does not offer.
  const canSeal = !space.sealed && looksEmpty;
  const confirmSeal = async () => {
    try {
      await sealSpace(space.space_id);
      showNotificationGlobal(`Space "${space.name}" sealed`, "success");
      setSealOpen(false);
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Seal failed"), "error");
    }
  };
  const confirmUnseal = async () => {
    try {
      await unsealSpace(space.space_id);
      showNotificationGlobal(`Space "${space.name}" unsealed`, "success");
      setUnsealOpen(false);
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Unseal failed"), "error");
    }
  };

  // ONE affordance gate for every act on this space: `can_manage_access` and
  // `can_delete` are both `manager or org_governor`, and seal is manager-gated
  // on top of the emptiness check. Not a boundary — the door decides and its
  // refusal stays authoritative — but offering the whole Access UI and a Delete
  // button to every viewer made each control a guaranteed toast.
  const governsSpace =
    org?.is_personal === true ||
    org?.role === "owner" ||
    org?.role === "org_admin" ||
    [...grants, ...parentGrants].some(
      (g) => g.subject === `user:${selfId}` && g.role === "manager",
    );

  // An Open space inherits its access and holds none of its own (D50), so its
  // people are decided one place only: the top of the tree. The door refuses a
  // grant here, so the controls give way to the way there.
  const inheritsAccess = space.classification === "open";

  const directUserGrants = grants.filter((g) => g.subject.startsWith("user:"));
  // Listed read-only so no grant that still confers access goes unseen.
  const nonUserGrants = grants.filter((g) => !g.subject.startsWith("user:"));

  return (
    <div className="flex h-full flex-col overflow-y-auto px-8">
      {/* Pinned, not centred: this page's height changes with every space, and
          a centred column would move the header on each switch. */}
      <div className="shrink-0" style={{ height: "4rem" }} />
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-6">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-1.5">
          <span className="flex items-center gap-2 item-height">
            <button
              type="button"
              onClick={() => navigate("/overview")}
              className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
            >
              <span className="truncate">{org?.name}</span>
            </button>
            {parent && (
              <>
                <span className="flex items-center text-fg-muted-regular">
                  /
                </span>
                <button
                  type="button"
                  onClick={() => navigate(`/spaces/${parent.space_id}`)}
                  className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
                >
                  <span className="truncate">{parent.name}</span>
                </button>
              </>
            )}
            <span className="flex items-center text-fg-muted-regular">/</span>
            <span className="ml-auto flex items-center">
              {governsSpace && !space.deletion_scheduled_at && (
                <TooltipButton
                  title="New space"
                  aria-label={`New space inside ${space.name}`}
                  variant="default"
                  size="xs"
                  onClick={() => setCreateInsideOpen(true)}
                >
                  <AddIcon fontSize="inherit" />
                </TooltipButton>
              )}
              <TooltipButton
                title="Close settings"
                aria-label="Close space settings"
                variant="default"
                size="xs"
                onClick={() => navigate("/overview")}
              >
                <CloseIcon fontSize="inherit" />
              </TooltipButton>
            </span>
          </span>
          <span className="flex items-center gap-2">
            <SpaceColorPicker
              color={spaceColor(space)}
              onPick={(color) => void pickColor(color)}
            />
            <span className="min-w-0 flex-1 text-fg-title">
              <EditableName
                name={space.name}
                isEditing={isEditingName}
                canEdit
                editTrigger="click"
                ariaLabel="Space name"
                maxLength={MAX_SPACE_NAME_CHARS}
                editColor={resolveDatasetColor(spaceColor(space))}
                setIsEditing={setIsEditingName}
                update={() => {}}
                commit={commitName}
                abort={() => setIsEditingName(false)}
              />
            </span>
            {space.sealed && (
              <Badge
                label={
                  <span className="flex items-center gap-1">
                    <LockOutlinedIcon sx={{ fontSize: 10 }} />
                    Sealed
                  </span>
                }
              />
            )}
          </span>
        </div>

        {/* ── Access ─────────────────────────────────────────────── */}
        <section className="flex flex-col">
          <SectionRule
            label="Access"
            trailing={`${grants.length} direct${
              parentGrants.length > 0
                ? ` · ${parentGrants.length} inherited`
                : ""
            }`}
          />
          <div className="flex flex-col gap-2">
            {governsSpace && isAbandoned(space) && (
              <Callout variant="warning" title="This space has no manager">
                <span className="flex flex-col items-start gap-2">
                  Whoever managed it is gone. Nobody owns the models inside it
                  or can grant access to it until you appoint someone.
                  {inheritsAccess && parent ? (
                    <MetaAddButton
                      label={`Appoint one on ${parent.name}`}
                      icon={<PersonAddAltOutlinedIcon className="icon-xs" />}
                      onClick={() => navigate(`/spaces/${parent.space_id}`)}
                    />
                  ) : (
                    <MetaAddButton
                      label="Assign manager"
                      icon={<PersonAddAltOutlinedIcon className="icon-xs" />}
                      onClick={() => setAssignOpen(true)}
                    />
                  )}
                </span>
              </Callout>
            )}
            {governsSpace && inheritsAccess && parent && (
              <Callout variant="info" title="Access comes from the space above">
                <span className="flex flex-col items-start gap-2">
                  Everyone here reaches this space through {parent.name}, and
                  nobody can be added to it on its own. Give someone a separate
                  set of people by making them a top-level space instead.
                  <MetaAddButton
                    label={`${MANAGE_ACCESS_MENU_LABEL} on ${parent.name}`}
                    icon={<CATALOG.manageAccess.Icon className="icon-xs" />}
                    onClick={() => navigate(`/spaces/${parent.space_id}`)}
                  />
                </span>
              </Callout>
            )}
            {governsSpace && !inheritsAccess && (
              <div className="flex items-center gap-2">
                <EmailChipInput
                  chips={grantChips}
                  onChipsChange={setGrantChips}
                  currentUserEmail={currentUserEmail}
                  directory={directory}
                  directoryLabel={org?.name}
                  alreadyGranted={grantedEmails}
                  error={grantError}
                  onErrorChange={setGrantError}
                  placeholder={
                    directory
                      ? `Members of ${org?.name ?? "the organization"}`
                      : "Grant by email…"
                  }
                  ariaLabel="Grant by email"
                />
                <button
                  ref={grantRoleAnchorRef}
                  onClick={() => setGrantRoleOpen((v) => !v)}
                  aria-expanded={grantRoleOpen}
                  className="menu-trigger w-28 shrink-0 gap-1.5"
                >
                  {roleLabel(grantRole)}
                  <UnfoldMoreIcon
                    sx={{ fontSize: 12 }}
                    className="shrink-0 text-fg-muted"
                  />
                </button>
                <DropdownMenu
                  anchorRef={grantRoleAnchorRef}
                  isOpen={grantRoleOpen}
                  onClose={() => setGrantRoleOpen(false)}
                  placement="bottom-end"
                >
                  {ROLES.map((role) => (
                    <DropdownMenuItem
                      key={role}
                      isSelected={role === grantRole}
                      onClick={() => {
                        setGrantRole(role);
                        setGrantRoleOpen(false);
                      }}
                    >
                      <span className="flex-1 text-left text-fg-regular">
                        {roleLabel(role)}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => void submitGrant()}
                  disabled={!grantChips.some((chip) => chip.status === "valid")}
                >
                  Grant
                </Button>
              </div>
            )}
            {grantError && <span className="text-error">{grantError}</span>}
            <div className="flex flex-col pt-2">
              {directUserGrants.map((grant) => (
                <GrantRow
                  key={`${grant.subject}-${grant.role}`}
                  grant={grant}
                  orgRole={
                    grant.email ? orgRoleByEmail.get(grant.email) : undefined
                  }
                  canManage={governsSpace}
                  canRemove={
                    !(org?.is_personal && grant.subject === `user:${selfId}`)
                  }
                  cannotStepDown={isYourOnlyManagerGrant(grant)}
                  onChangeRole={(role) => void changeRole(grant, role)}
                  onRemove={() =>
                    strandsTheSpace(grant)
                      ? setRevoking(grant)
                      : void removeAccess(grant)
                  }
                />
              ))}
              {nonUserGrants.map((grant) => (
                <div
                  key={`${grant.subject}-${grant.role}`}
                  className="flex h-8 items-center gap-2 rounded-app px-2"
                >
                  <span className="min-w-0 flex-1 truncate text-fg-regular">
                    {grant.subject}
                  </span>
                  <span className="text-fg-muted-regular-small">
                    {roleLabel(grant.role)}
                  </span>
                </div>
              ))}
              {grants.length === 0 && (
                <span className="px-2 py-2 text-fg-muted-regular-small">
                  No direct grants.
                </span>
              )}
            </div>
            {parentGrants.length > 0 && parent && (
              <div className="flex flex-col">
                <SectionRule label={`Inherited from ${parent.name}`} />
                <div className="flex flex-col gap-1">
                  {parentGrants.map((grant) => (
                    <div
                      key={`${grant.subject}-${grant.role}`}
                      className="flex h-7 items-center gap-2 rounded-app px-2 opacity-60"
                    >
                      <span className="min-w-0 flex-1 truncate text-fg-regular">
                        {grant.name || grant.email || grant.subject}
                      </span>
                      <span className="text-fg-muted-regular-small">
                        {roleLabel(grant.role)}
                      </span>
                      <span
                        className="font-mono text-fg-muted uppercase"
                        style={{
                          fontSize: "var(--font-size-xx-small)",
                          letterSpacing: "0.05em",
                        }}
                      >
                        change at parent
                      </span>
                    </div>
                  ))}
                  <span className="px-2 text-fg-muted-regular-small">
                    Additive. Inherited grants cannot be removed here.
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Configuration ──────────────────────────────────────── */}
        <section className="flex flex-col">
          <SectionRule label="Configuration" />
          <div className="flex min-h-8 items-center gap-2 px-2">
            <span className="w-33 shrink-0 text-fg-muted-medium">
              Classification
            </span>
            <span className="text-fg-regular capitalize">
              {space.classification}
            </span>
          </div>
          {/* D39: what a closed ANCESTOR imposes, which the space's own
              classification does not say. A child of a sealed space is walled
              and hidden however it was created — stating only its own preset
              would read as a promise it doesn't hold. */}
          {parent &&
            (parent.classification === "sealed" ||
              parent.classification === "restricted") && (
              <div className="flex min-h-8 items-start gap-2 px-2 py-1">
                <span className="w-33 shrink-0 text-fg-muted-medium">
                  Inherited from {parent.name}
                </span>
                <span className="text-fg-regular">
                  {parent.classification === "sealed"
                    ? `Walled and hidden — inside a sealed space, nothing leaves and only ${parent.name}'s people see this`
                    : `Hidden org-wide — only ${parent.name}'s people see this`}
                </span>
              </div>
            )}
          <div className="flex min-h-8 items-center gap-2 px-2">
            <span className="w-33 shrink-0 text-fg-muted-medium">
              Access inheritance
            </span>
            <span className="text-fg-regular">
              {space.classification !== "open"
                ? "Nothing is inherited; every grant is explicit"
                : parent
                  ? `Inherits from ${parent.name}`
                  : "Top level"}
            </span>
            {(space.sealed || canSeal) && (
              <span className="ml-auto shrink-0">
                <Badge
                  label={
                    space.sealed
                      ? "org admins can unseal"
                      : "sealable while empty"
                  }
                />
              </span>
            )}
          </div>
        </section>

        {/* ── Governance (company orgs, org admins) ──────────────── */}
        {audit.available === true && (
          <section className="flex flex-col">
            <SectionRule
              label="Governance"
              action={
                audit.entries.length > 0
                  ? {
                      label: "Show all",
                      onClick: () => setAuditPanelOpen(true),
                    }
                  : undefined
              }
            />
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-1">
                <span className="text-fg-muted-regular-small">
                  Org admins govern this space but cannot read it.
                </span>
                <TooltipButton
                  title="Admins can rename, delete and appoint managers. Reading content requires a grant, which appears in Access like any other."
                  size="xs"
                  variant="default"
                >
                  <InfoOutlinedIcon />
                </TooltipButton>
              </span>
              <GovernanceAuditList
                entries={audit.entries}
                names={audit.names}
              />
            </div>
          </section>
        )}

        {/* ── Danger ─────────────────────────────────────────────── */}
        {governsSpace && (
          <section className="flex flex-col">
            <SectionRule label="Danger" />
            {space.deletion_scheduled_at ? (
              <div className="flex items-center gap-3 px-2 py-2">
                <span className="min-w-0 flex-1 text-fg-regular">
                  {space.deletion_started_at
                    ? "The teardown is running now. Everything inside this space is on its way out, and it can no longer be stopped."
                    : `In the Trash until ${purgeDate(space.deletion_scheduled_at)}, its sub-spaces and models with it. Managers restore it from the Trash — or cancel here.`}
                </span>
                <TooltipButton
                  title={
                    space.deletion_started_at
                      ? "The teardown has started — there is nothing left to cancel."
                      : "Cancel deletion — the space goes back to normal."
                  }
                  aria-label="Cancel deletion"
                  variant="contextual"
                  size="xs"
                  disabled={!!space.deletion_started_at}
                  onClick={() => void undoDeletion()}
                >
                  <SettingsBackupRestoreIcon fontSize="inherit" />
                </TooltipButton>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {canSeal && (
                  <TooltipButton
                    title="Only an empty space can be sealed. Sealing cannot contain data that has already been shared out."
                    variant="secondary"
                    className={DANGER_BUTTON_WIDTH}
                    onClick={() => setSealOpen(true)}
                  >
                    Seal space
                  </TooltipButton>
                )}
                {space.sealed && canUnseal && (
                  <TooltipButton
                    title="Org admins only. Declassification is recorded in the governance audit log."
                    variant="secondary"
                    className={DANGER_BUTTON_WIDTH}
                    onClick={() => setUnsealOpen(true)}
                  >
                    Unseal space
                  </TooltipButton>
                )}
                <TooltipButton
                  title="Managers and org admins can delete. The space, its sub-spaces, their models and tables go to the Trash for 30 days, and come back together if restored."
                  variant="destructive"
                  className={DANGER_BUTTON_WIDTH}
                  onClick={openDelete}
                >
                  Delete space
                </TooltipButton>
              </div>
            )}
          </section>
        )}
      </div>
      <ColumnBottomSpacer />

      <Dialog
        open={sealOpen}
        title={`Seal "${space.name}"?`}
        onClose={() => setSealOpen(false)}
      >
        <span className="text-fg-regular">
          Nothing will inherit in. The space becomes invisible to non-members
          and its data cannot leave: no exports, no copies, no outbound imports,
          no per-model sharing. Access is granted on the space itself.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setSealOpen(false),
            },
            {
              label: "Seal space",
              variant: "primary",
              onClick: () => void confirmSeal(),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={unsealOpen}
        title={`Unseal "${space.name}"?`}
        onClose={() => setUnsealOpen(false)}
      >
        <span className="text-fg-regular">
          Exports, copies and sharing are re-enabled. Sub-spaces that only
          inherited the wall are unsealed; sub-spaces sealed in their own right
          are unaffected. Inherited access is not restored and existing grants
          are unchanged. Recorded in the governance audit log.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setUnsealOpen(false),
            },
            {
              label: "Unseal space",
              variant: "primary",
              onClick: () => void confirmUnseal(),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={deleteOpen}
        title={`Delete "${space.name}"?`}
        onClose={() => setDeleteOpen(false)}
      >
        {(() => {
          if (analysisError) {
            return (
              <span
                className="text-error"
                data-testid="deletion-analysis-error"
              >
                {analysisError}
              </span>
            );
          }
          if (!analysis) {
            return (
              <span
                className="text-fg-muted-regular"
                data-testid="deletion-analysis-loading"
              >
                Checking what this deletes…
              </span>
            );
          }
          const nonEmpty =
            analysis.models.length > 0 ||
            analysis.children.length > 0 ||
            analysis.people_losing_access > 0 ||
            guestCount > 0;
          return (
            <>
              <span className="pb-1 text-fg-muted-regular">
                {nonEmpty
                  ? "This schedules the space for deletion:"
                  : "This schedules the space for deletion."}
              </span>
              <div className="flex flex-col gap-3 pt-1">
                {analysis.models.length > 0 && (
                  <ImpactRow
                    icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.models.length}
                    label={`${plural(analysis.models.length, "model")} · ${analysis.total_tables} ${plural(analysis.total_tables, "table")}`}
                  />
                )}
                {analysis.critical_tables > 0 && (
                  <ImpactRow
                    tone="warning"
                    icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.critical_tables}
                    label={`${plural(analysis.critical_tables, "table")} imported elsewhere`}
                    hint="Models outside this space lose their source."
                  />
                )}
                {analysis.children.length > 0 && (
                  <ImpactRow
                    icon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.children.length}
                    label={`${plural(analysis.children.length, "sub-space")} — go with it`}
                  >
                    <div className="flex flex-col pt-1">
                      {analysis.children.map((child) => (
                        <span
                          key={child.space_id}
                          className="flex items-center item-height text-fg-regular"
                        >
                          {child.name}
                        </span>
                      ))}
                    </div>
                  </ImpactRow>
                )}
                {analysis.people_losing_access > 0 && (
                  <ImpactRow
                    icon={<PeopleOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.people_losing_access}
                    label={`${analysis.people_losing_access === 1 ? "person loses" : "people lose"} access`}
                  >
                    <div className="flex flex-col pt-1">
                      {losingAccess.slice(0, 3).map((who) => (
                        <span
                          key={who.key}
                          className="flex items-center gap-2 item-height"
                        >
                          <span className="min-w-0 flex-1 truncate text-fg-regular">
                            {who.label}
                          </span>
                          <span className="shrink-0 text-fg-muted-regular-small">
                            {roleLabel(who.role)}
                          </span>
                        </span>
                      ))}
                      {losingAccess.length > 3 && (
                        <span className="text-fg-muted-regular-small">
                          and {losingAccess.length - 3} other
                          {losingAccess.length - 3 === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </ImpactRow>
                )}
                {guestCount > 0 && (
                  <ImpactRow
                    tone="warning"
                    icon={<PersonOffOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={guestCount}
                    label={`guest ${plural(guestCount, "grant")}`}
                    hint="The only access those people hold."
                  />
                )}
              </div>
              {nonEmpty && (
                <label className="flex flex-col gap-1 pt-2">
                  <span className="text-fg-muted-regular-small">
                    Type the space name to confirm
                  </span>
                  <TextInput
                    value={typedName}
                    onChange={setTypedName}
                    placeholder={space.name}
                    ariaLabel="Type the space name to confirm"
                  />
                </label>
              )}
              <span className="text-fg-muted-regular">
                Everything here goes to the Trash now — the space, its
                sub-spaces, their models and tables — for {GRACE_DAYS} days. Any
                manager can restore it from the Trash, and all of it comes back
                together.
              </span>
            </>
          );
        })()}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setDeleteOpen(false),
            },
            {
              label: "Delete space",
              variant: "primary",
              onClick: () => void confirmDelete(),
              disabled:
                !analysis ||
                ((analysis.models.length > 0 ||
                  analysis.children.length > 0 ||
                  analysis.people_losing_access > 0 ||
                  guestCount > 0) &&
                  typedName.trim() !== space.name),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={revoking !== null}
        title={`Remove ${revoking ? grantLabel(revoking) : ""}'s access?`}
        onClose={() => setRevoking(null)}
      >
        <span className="pb-1 text-fg-muted-regular">
          They are the only manager, so this leaves the space with none:
        </span>
        <div className="flex flex-col gap-3 pt-1">
          <ImpactRow
            tone="warning"
            icon={<PersonOffOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Nobody owns the models inside it"
            hint="Until an org owner or admin appoints a manager."
          />
        </div>
        <span className="text-fg-muted-regular">
          You can grant someone else manager first, or appoint one afterwards
          from this page — an org governor never loses that right.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setRevoking(null),
            },
            {
              label: "Remove anyway",
              variant: "primary",
              onClick: () => {
                const grant = revoking;
                setRevoking(null);
                if (grant) void removeAccess(grant);
              },
            },
          ]}
        />
      </Dialog>

      <NewSpaceOverlay
        open={createInsideOpen}
        onClose={() => setCreateInsideOpen(false)}
        parentId={space.space_id}
      />

      <AssignManagerDialog
        open={assignOpen}
        space={space}
        members={members}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => {
          void refreshSpaces();
          reloadGrants();
        }}
      />

      <GovernanceAuditPanel
        open={auditPanelOpen}
        onClose={() => setAuditPanelOpen(false)}
        orgId={org && !org.is_personal ? org.org_id : null}
        spaceId={space_id}
        title={`Governance · ${space.name}`}
      />
    </div>
  );
};

const GrantRow: React.FC<{
  grant: SpaceGrant;
  /// `owner` / `org_admin` when the grantee governs this space through the org
  /// (D3) whatever their space role says.
  orgRole: string | undefined;
  canManage: boolean;
  /// A personal org's spaces are its owner's to manage — there is no second
  /// governor to reappoint them, so revoking their own grant is offered to
  /// nobody (D49 allows stranding only because it stays recoverable).
  canRemove: boolean;
  /// Your own grant, and the only manager the space has (D66).
  cannotStepDown: boolean;
  onChangeRole: (role: string) => void;
  onRemove: () => void;
}> = ({
  grant,
  orgRole,
  canManage,
  canRemove,
  cannotStepDown,
  onChangeRole,
  onRemove,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isSelf = grant.subject === `user:${localStorage.getItem("clientID")}`;
  return (
    <div className="group flex h-8 items-center gap-2 rounded-app px-2 hover:bg-list-hover">
      <span className="min-w-0 flex-1 truncate">
        <span className="text-fg-regular">{grantLabel(grant)}</span>
        {isSelf && <span className="pl-1 text-fg-muted-regular">(You)</span>}
        {grant.name && grant.email && (
          <span className="pl-2 text-fg-muted-regular-small">
            {grant.email}
          </span>
        )}
      </span>
      {grant.is_org_member === false && (
        <Badge label="Guest" className="shrink-0" />
      )}
      {orgRole && (
        <Tooltip
          title={`${
            orgRole === "owner" ? "Organization owner" : "Organization admin"
          }\nGoverns every space in the org: can grant, revoke and delete here whatever the role beside this says.`}
        >
          <Badge
            label={orgRole === "owner" ? "Owner" : "Org admin"}
            className="shrink-0"
          />
        </Tooltip>
      )}
      {!canManage ? (
        <span className="w-32 shrink-0 text-fg-muted-regular-small">
          {roleLabel(grant.role)}
        </span>
      ) : (
        <button
          ref={anchorRef}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`Change access for ${grantLabel(grant)}`}
          className="menu-trigger-sm w-32 shrink-0 gap-1.5"
        >
          {roleLabel(grant.role)}
          <UnfoldMoreIcon
            sx={{ fontSize: 12 }}
            className="shrink-0 text-fg-muted"
          />
        </button>
      )}
      <DropdownMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        placement="bottom-end"
      >
        {ROLES.map((role) => {
          const locked = cannotStepDown && role !== "manager";
          return (
            <DropdownMenuItem
              key={role}
              isSelected={role === grant.role}
              disabled={locked}
              hint={locked ? ONLY_MANAGER_HINT : undefined}
              onClick={() => {
                setOpen(false);
                if (role !== grant.role) onChangeRole(role);
              }}
            >
              <span
                className={`flex-1 text-left ${locked ? "text-fg-muted" : "text-fg-regular"}`}
              >
                {roleLabel(role)}
              </span>
              {role === "manager" && (
                <span className="text-fg-muted-regular-small">owns models</span>
              )}
              {role === grant.role && (
                <CheckIcon sx={{ fontSize: 12 }} className="text-accent" />
              )}
            </DropdownMenuItem>
          );
        })}
        {canRemove && (
          <>
            <div className="mx-2 my-1 h-px bg-[var(--color-border)]" />
            <DropdownMenuItem
              disabled={cannotStepDown}
              hint={cannotStepDown ? ONLY_MANAGER_HINT : undefined}
              onClick={() => {
                setOpen(false);
                onRemove();
              }}
            >
              <span
                className={`flex flex-1 items-center gap-1.5 text-left ${cannotStepDown ? "text-fg-muted" : "text-error"}`}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
                Remove access
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenu>
    </div>
  );
};
